'use client';

import { styled } from '@linaria/react';
import { NewsTagGtmEventType } from '@fe-common-ui/Button';
import { QuoteGtmEventType, TagsGroup } from '@fe-common-ui/Group';
import Link from 'next/link';
import { GTM_EVENT_POP_TOPIC_CAROUSEL } from '@fe-news/constants/pop-topic/type';
import { GTMEventKey, sendGTM, CustomEventName, GaActionType } from '@fe-cnyes/fe-common-gtm';
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import FollowButton from '@fe-news/components/atoms/PopTopic/FollowButton';
import useMediaQuery from '@fe-news/hooks/use-media-query';

const TitleWrapper = styled.div<{ bgImage: string }>`
  padding: 16px 16px 24px 12px;
  position: relative;
  overflow: hidden;
  margin-bottom: 27px;
  border-radius: 6px 6px 0px 0px;

  &::before {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 0;
    opacity: 0.6;
    content: '';
    position: absolute;
    background-size: cover;
    background-position: center;
    background-image: ${({ bgImage }) => `url(${bgImage})`};
  }

  &:hover {
    &::before {
      transition: 0.3s;
      transform: scale(1.2);
    }
  }
`;

const TitleContent = styled.div`
  position: relative;
  z-index: 1;
  color: #fff;
`;

const Title = styled.div`
  display: flex;
  min-height: 64px;
  align-items: center;

  span {
    font-size: 24px;
  }
`;

const H3 = styled.h3<{ isCarousel: boolean }>`
  flex: 1;
  margin-left: ${({ isCarousel }) => (isCarousel ? '28px' : '4px')};
  margin-right: 16px;

  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  @media screen and (max-width: ${SCREEN_SIZE.DESKTOP}px) {
    -webkit-line-clamp: 3;
  }

  a {
    color: #fff;
    text-decoration: none;
    font-size: 24px;
    font-weight: 600;
    line-height: 1.45;
    letter-spacing: 0.5px;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Summary = styled.div`
  margin-top: 4px;
  padding-left: 28px;
  font-size: 14px;
  line-height: 1.71;
  text-align: justify;
  opacity: 0.8;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TagWrapper = styled.div`
  margin-top: 10px;
  padding-left: 28px;
`;

const BackgroundCover = styled.div`
  background-color: #000;
  opacity: 0.6;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

type PopTopicCardTitleProps = {
  title: string;
  summary: string;
  tags: string[] | null;
  id: number;
  bgImage: string;
  isCarousel?: boolean;
  gtmEvent?: GaActionType;
};

const PopTopicCardTitle: React.FC<PopTopicCardTitleProps> = ({
  title,
  summary,
  tags,
  id,
  isCarousel = false,
  bgImage,
  gtmEvent
}) => {
  const isDesktop = useMediaQuery(`(min-width:${SCREEN_SIZE.DESKTOP}px)`, true);

  const onClickTitle = (title: string) => {
    if (gtmEvent) return sendGTM(gtmEvent);
    sendGTM({
      ...GTM_EVENT_POP_TOPIC_CAROUSEL,
      [GTMEventKey.CLICK_ITEM]: isCarousel ? `置頂時事_${title}` : title,
      [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_POP
    });
  };

  return (
    <TitleWrapper bgImage={bgImage}>
      <BackgroundCover />
      <TitleContent>
        <Title>
          {!isCarousel && (
            <span role='img' aria-label='fire-emoji'>
              🔥
            </span>
          )}
          <H3 isCarousel={isCarousel}>
            <Link onClick={() => onClickTitle(title)} href={`/poptopic/${id}`} prefetch={false}>
              {title}
            </Link>
          </H3>
          {isDesktop && (
            <FollowButton
              id={id}
              isDetail={false}
              gtmEvent={{
                [GTMEventKey.EVENT_NAME]: CustomEventName.FOLLOW_POP,
                [GTMEventKey.SECTION]: 'News_Category_時事',
                [GTMEventKey.CLICK_ITEM]: `熱門時事＿${title}`
              }}
              isNeedAutoUnFollow={title === '' || title === '404'}
            />
          )}
        </Title>
        <Summary dangerouslySetInnerHTML={{ __html: summary }}></Summary>
        {tags && (
          <TagWrapper>
            <TagsGroup
              tags={tags}
              gtmEventNewsTagType={NewsTagGtmEventType.NEWS_CATEGORY_POP_TOPIC}
              gtmEventQuoteType={QuoteGtmEventType.NEWS_CATEGORY_POP_TOPIC}
              backgroundColor='transparent'
              fontColor='#fff'
              isPopTopic
            />
          </TagWrapper>
        )}
      </TitleContent>
    </TitleWrapper>
  );
};

export default PopTopicCardTitle;
